import { Container, Grid, Typography } from '@mui/material';
import { ODSDivider } from '@telekom-ods/react-ui-kit';
import { Form, Formik, type FormikHelpers } from 'formik';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';

import Provision from '../../../api/provision';
import Button from '../../../components/Buttons/Button';
import { FormikTextField } from '../../../components/Formik';
import SuccessModal from '../../../components/Modal/SuccessModal';
import useApiQuery from '../../../hooks/useApiQuery';

import CloudSelect from './components/CloudSelect';
import NetworkConnectivity from './components/NetworkConnectivity';
import ProductSelect from './components/ProductSelect';
import ProjectSelect from './components/ProjectSelect';
import ResourceQuota from './components/ResourceQuota';
import { getValidationSchema } from './validation';

import type { APIHookArg } from '@/types';
import type { ComputeOrderValues, Product, UnusedQuota } from '@/types/productTypes';

const ramToMB = (ram: string): number => Number(ram) * 1024;

const initialValues: ComputeOrderValues = {
  osProjectName: '',
  projectId: '',
  productId: '',
  cloudName: '',
  cpuCores: '',
  ram: '',
  diskQuota: '',
  floatingIps: '',
  internetConnectivity: false,
  DTHConnectivity: false,
  DTHRedRouterConnectivity: false,
  juiceGroup: '',
  unusedQuota: {
    compute: { cores: '', ram: '' },
    volume: { gigabytes: '', objectsMegabytes: 0 },
    network: {
      floatingIps: '',
      DTHRedRouterConnectivity: false,
      DTHConnectivity: false,
      internetConnectivity: false,
    },
  } as unknown as UnusedQuota,
  externalNetworks: [] as string[],
  buttonHelperText: '',
};

const numberInputOnWheelPreventChange = (event: React.WheelEvent<HTMLInputElement>): void => {
  const target = event.target as HTMLInputElement;
  target.blur();
  event.stopPropagation();
  setTimeout(() => {
    target.focus();
  }, 0);
};

const ComputeOrder = (): React.JSX.Element => {
  const intl = useIntl();
  const navigate = useNavigate();
  const validationSchema = getValidationSchema(intl);

  const [osProjectOrdered, setOsProjectOrdered] = useState<boolean>(false);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);

  const { makeApiCall: createOsProject } = useApiQuery(
    Provision.createOsProject as unknown as APIHookArg
  );

  const handleSubmit = async (
    values: ComputeOrderValues,
    _formikHelpers: FormikHelpers<ComputeOrderValues>
  ): Promise<void> => {
    const { buttonHelperText, ...submitData } = values;
    const data = await createOsProject({
      type: 'compute',
      ...submitData,
      cloudId: values.cloudName,
      requestedQuota: {
        compute: {
          cores: values.cpuCores,
          ram: ramToMB(values.ram),
        },
        network: {
          floatingIps: values.floatingIps,
          DTHRedRouterConnectivity: values.DTHRedRouterConnectivity,
          DTHConnectivity: values.DTHConnectivity,
          internetConnectivity: values.internetConnectivity,
        },
        volume: {
          gigabytes: values.diskQuota,
          objectsMegabytes: 0,
        },
      },
    });

    if (data !== null) {
      setOsProjectOrdered(true);
    }
  };

  return (
    <>
      {osProjectOrdered && (
        <SuccessModal
          title={intl.formatMessage({ id: 'computeOsProjectOrdered' })}
          messageId='createOsProjectFulfilled'
          onSubmit={() => {
            navigate('/products/compute');
          }}
          onClose={() => {
            navigate('/products/compute');
          }}
        />
      )}
      <Container>
        <Typography
          variant='h4'
          color='primary'
          gutterBottom
        >
          {intl.formatMessage({ id: 'openstackProjectRequest' })}
        </Typography>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          validateOnChange
          validateOnBlur
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, values, isValid, dirty }) => (
            <Form>
              <Grid
                container
                justifyContent='space-between'
                alignItems='flex-start'
              >
                {/* Project Selection */}
                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <Typography>
                    <strong>{intl.formatMessage({ id: 'projectLabel' })}</strong>
                    {intl.formatMessage({ id: 'projectDescription' })}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={4}
                >
                  <ProjectSelect setFilteredProducts={setFilteredProducts} />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sx={{ marginBottom: '1rem' }}
                >
                  <ODSDivider variant='horizontal' />
                </Grid>

                {/* Product Selection */}
                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <Typography>
                    <strong>{intl.formatMessage({ id: 'productLabel' })}</strong>
                    {intl.formatMessage({ id: 'productDescription' })}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={4}
                >
                  <ProductSelect filteredProducts={filteredProducts} />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sx={{ marginBottom: '1rem' }}
                >
                  <ODSDivider variant='horizontal' />
                </Grid>

                {/* Cloud Selection */}
                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <Typography>
                    <strong>{intl.formatMessage({ id: 'dataCenterLabel' })}</strong>
                    {intl.formatMessage({ id: 'dataCenterDescription' })}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={4}
                >
                  <CloudSelect />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sx={{ marginBottom: '1rem' }}
                >
                  <ODSDivider variant='horizontal' />
                </Grid>

                {/* OSProject Name */}
                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <Typography>
                    <strong>{intl.formatMessage({ id: 'projectNameLabel' })}</strong>
                    {intl.formatMessage({ id: 'projectName' })}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={4}
                >
                  <FormikTextField
                    name='osProjectName'
                    label={intl.formatMessage({ id: 'osProjectName' })}
                    placeholder={intl.formatMessage({ id: 'osProjectNamePlaceholder' })}
                    disabled={!values.cloudName}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sx={{ marginBottom: '1rem' }}
                >
                  <ODSDivider variant='horizontal' />
                </Grid>

                {/* Resource Quota */}
                <ResourceQuota
                  unusedQuota={values.unusedQuota}
                  onWheel={numberInputOnWheelPreventChange}
                />

                {/* Network Connectivity */}
                <Grid
                  item
                  xs={12}
                  sx={{ marginBottom: '1rem' }}
                >
                  <ODSDivider variant='horizontal' />
                </Grid>
                <NetworkConnectivity externalNetworks={values.externalNetworks} />
                <Grid
                  item
                  xs={12}
                  sx={{ marginBottom: '1rem' }}
                >
                  <ODSDivider variant='horizontal' />
                </Grid>
                <Grid
                  container
                  justifyContent='end'
                  alignItems='center'
                  spacing={2}
                >
                  {values.buttonHelperText && (
                    <Grid item>
                      <Typography color='error'>{values.buttonHelperText}</Typography>
                    </Grid>
                  )}
                  <Grid item>
                    <Button
                      type='submit'
                      disabled={isSubmitting || !isValid || !dirty}
                      label={intl.formatMessage({ id: 'submit' })}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Container>
    </>
  );
};

export default ComputeOrder;
